// Eskom PARCC — button primitive.
//
// Wraps the monolith's `.btn` / `.btn.primary` / `.btn.ghost` styles. Forwards
// every native button prop (onClick, disabled, type, aria-*, …); `variant`
// selects the visual treatment. Pages that need a navigating control should use
// react-router's <Link> directly (kept out of scope here on purpose).
import type { ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger';
/** Visual size — `sm` is the compact treatment for dense toolbars (e.g. export buttons). */
export type ButtonSize = 'sm' | 'md';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  default: 'btn',
  primary: 'btn primary',
  ghost: 'btn ghost',
  // `.btn.danger` already exists in components.css (revoke access etc.); it just
  // had no typed variant, so callers were hand-writing the class.
  danger: 'btn danger',
};

export function Button({
  variant = 'default',
  size = 'md',
  type = 'button',
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
}): React.JSX.Element {
  const classes = [VARIANT_CLASS[variant], size === 'sm' && 'sm', className]
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={classes} {...rest}>
      {children}
    </button>
  );
}
